import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { apiMessage } from '@/lib/pricing';

type RegistryKey = {
  id: string;
  label: string;
  prefix: string;
  created_at: string;
  created_by_email: string;
  last_used_at: string | null;
  revoked_at: string | null;
};

type Kind = {
  /** Used for the endpoint, the query key and every test id. */
  slug: string;
  title: string;
  what: string;
  description: string;
  placeholder: string;
  /** What to do with the secret once, since the two go to different places. */
  keeping: string;
};

function when(value: string | null) {
  if (!value) return 'never';
  return new Date(value).toLocaleDateString();
}

function KeyCard({ kind }: { kind: Kind }) {
  const queryClient = useQueryClient();
  const [creating, setCreating] = React.useState(false);
  const [label, setLabel] = React.useState('');
  const [secret, setSecret] = React.useState<string | null>(null);

  const endpoint = `/global-admin/${kind.slug}-keys/`;
  const queryKey = [`${kind.slug}-keys`];

  const keys = useQuery({
    queryKey,
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get(endpoint)).data.keys as RegistryKey[];
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey });

  const create = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.post(endpoint, { label })).data.secret as string;
    },
    onSuccess: (raw) => {
      setCreating(false);
      setLabel('');
      setSecret(raw);
      refresh();
    },
    onError: (error) => toast.error(apiMessage(error)),
  });

  const revoke = useMutation({
    mutationFn: async (key: RegistryKey) => {
      const api = await authenticatedBitswanBackendInstance();
      await api.delete(`${endpoint}${key.id}/`);
    },
    onSuccess: () => {
      refresh();
      toast.success('Key revoked');
    },
    onError: (error) => toast.error(apiMessage(error)),
  });

  return (
    <Card data-testid={`${kind.slug}-keys-card`}>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{kind.title}</CardTitle>
            <CardDescription className="mt-1.5">{kind.description}</CardDescription>
          </div>
          <Button onClick={() => setCreating(true)} data-testid={`create-${kind.slug}-key`}>
            New {kind.what}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {keys.isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (keys.data ?? []).length === 0 ? (
          <p className="py-6 text-center text-muted-foreground">No {kind.what}s yet.</p>
        ) : (
          <Table data-testid={`${kind.slug}-key-list`}>
            <TableHeader>
              <TableRow>
                <TableHead>What for</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Issued</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(keys.data ?? []).map((key) => (
                <TableRow key={key.id} data-testid={`${kind.slug}-key-${key.id}`}>
                  <TableCell>{key.label}</TableCell>
                  <TableCell>
                    <code className="rounded bg-muted px-2 py-0.5">{key.prefix}…</code>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{when(key.created_at)}</TableCell>
                  <TableCell className="text-muted-foreground">{when(key.last_used_at)}</TableCell>
                  <TableCell className="text-right">
                    {key.revoked_at ? (
                      <Badge className="border-red-200 bg-red-50 text-red-700">revoked</Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                        onClick={() => revoke.mutate(key)}
                        data-testid={`revoke-${kind.slug}-key-${key.id}`}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid={`${kind.slug}-key-dialog`}>
          <DialogHeader>
            <DialogTitle>New {kind.what}</DialogTitle>
            <DialogDescription>Say what it is for; nobody will remember later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`${kind.slug}-key-label`}>What it is for</Label>
            <Input
              id={`${kind.slug}-key-label`}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={kind.placeholder}
              data-testid={`${kind.slug}-key-label`}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button
              disabled={!label.trim() || create.isPending}
              onClick={() => create.mutate()}
              data-testid={`${kind.slug}-key-save`}
            >
              {create.isPending ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={secret !== null} onOpenChange={() => setSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>The {kind.what}</DialogTitle>
            <DialogDescription>
              {kind.keeping} Only its first characters are stored, so it cannot be shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code
              className="flex-1 break-all rounded bg-muted p-3 text-sm"
              data-testid={`${kind.slug}-key-secret`}
            >
              {secret}
            </code>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                navigator.clipboard.writeText(secret ?? '');
                toast.success('Copied');
              }}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setSecret(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const PUSH: Kind = {
  slug: 'push',
  title: 'Push keys',
  what: 'push key',
  placeholder: 'Forgejo CI',
  description:
    'What our own CI publishes images and releases with. A push key can pull and push every '
    + 'image in the registry, so issue as few as possible and revoke one the moment it stops '
    + 'being used.',
  keeping: 'Copy it into the CI secret now.',
};

const MIRROR: Kind = {
  slug: 'mirror',
  title: 'Mirror keys',
  what: 'mirror key',
  placeholder: 'staging-freeze',
  description:
    'What the AOC below us in the release chain reads our releases and images with. It reaches '
    + 'the four AOC images and cannot push. Unlike a customer key there is no licence behind it, '
    + 'so nothing in billing can stop a release reaching production.',
  keeping: 'Give it to that AOC now.',
};

export default function RegistryKeysPage() {
  return (
    <div className="space-y-6" data-testid="registry-keys-page">
      <div>
        <h1 className="text-2xl font-semibold">Registry keys</h1>
        <p className="text-sm text-muted-foreground">
          The keys we issue to ourselves rather than to a customer: what CI publishes with, and
          what the next AOC down the chain reads with.
        </p>
      </div>

      <KeyCard kind={PUSH} />
      <KeyCard kind={MIRROR} />
    </div>
  );
}
